import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const TCPA_ATTESTATION_TEXT = `
SMS/TCPA COMPLIANCE ATTESTATION

By checking the box below, I hereby attest and confirm that:

1. CONSENT COLLECTION: I have obtained, or will obtain, express written consent from all contacts before sending any SMS messages through this platform. This consent includes:
   - Clear disclosure that the person is agreeing to receive text messages
   - Identification of my business name
   - Disclosure of message frequency
   - Disclosure that message and data rates may apply
   - Instructions for opting out (Reply STOP)

2. CONSENT DOCUMENTATION: I maintain, or will maintain, proper records of all consent obtained, including:
   - Date and time of consent
   - Method of consent collection (web form, paper form, verbal, etc.)
   - The exact consent language presented to the customer
   - IP address (for online consent)

3. COMPLIANCE WITH LAWS: I understand and agree to comply with:
   - The Telephone Consumer Protection Act (TCPA)
   - The CAN-SPAM Act
   - All applicable federal and state regulations regarding SMS marketing
   - Industry best practices for SMS messaging

4. PROHIBITED CONTENT: I will not send messages containing:
   - SHAFT content (Sex, Hate, Alcohol, Firearms, Tobacco)
   - Cannabis or CBD marketing
   - Payday loans or debt collection
   - Phishing, fraud, or misleading content

5. MESSAGE FREQUENCY: I will limit SMS messages to reasonable frequencies (recommended maximum of 4 messages per month per contact).

6. OPT-OUT COMPLIANCE: I will immediately honor all opt-out requests (STOP messages) and will not contact opted-out individuals again.

7. LIABILITY: I understand that I am solely responsible for any violations of TCPA or related regulations, and that such violations can result in fines up to $1,500 per message. I agree to indemnify and hold harmless the platform provider from any such violations.

8. ACCOUNT SUSPENSION: I understand that failure to maintain compliance may result in immediate suspension of my account without notice.

This attestation is legally binding and will be timestamped and recorded with my IP address and user agent information.
`;

interface ComplianceAttestationModalProps {
  open: boolean;
  onComplete: () => void;
}

export const ComplianceAttestationModal: React.FC<ComplianceAttestationModalProps> = ({
  open,
  onComplete,
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Debug: Log if redirect flag exists when modal opens
  React.useEffect(() => {
    if (open) {
      const redirectFlag = localStorage.getItem('redirect_to_checkout_after_attestation');
      const plan = localStorage.getItem('checkout_plan');
      console.log('ComplianceAttestationModal: Modal opened, redirect flag:', redirectFlag, 'plan:', plan);
      console.log('ComplianceAttestationModal: User authenticated:', isAuthenticated, 'user:', user?.email);
    }
  }, [open, isAuthenticated, user]);

  const handleSubmit = async () => {
    if (!agreed) {
      setError('You must agree to the terms to continue');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await api.compliance.createAttestation({
        attestation_type: 'tcpa_consent',
        attestation_text: TCPA_ATTESTATION_TEXT,
        accepted: true,
      });

      const data = response.data || response;
      console.log('ComplianceAttestationModal: Attestation response:', data);
      
      // Check if we need to redirect to checkout after attestation
      // This takes priority over needs_plan_selection since user came from email verification
      // Check this FIRST before any other logic - check multiple times to be sure
      const redirectToCheckout = localStorage.getItem('redirect_to_checkout_after_attestation');
      const planFromStorage = localStorage.getItem('checkout_plan');
      console.log('🔍 ComplianceAttestationModal: Checking redirect flag AFTER attestation');
      console.log('  - redirectToCheckout flag:', redirectToCheckout);
      console.log('  - planFromStorage:', planFromStorage);
      console.log('  - isAuthenticated:', isAuthenticated);
      console.log('  - user email:', user?.email);
      
      // Also check if flag was set in sessionStorage as backup
      const sessionFlag = sessionStorage.getItem('redirect_to_checkout_after_attestation');
      console.log('  - sessionStorage flag:', sessionFlag);
      
      if (redirectToCheckout === 'true' || sessionFlag === 'true') {
        console.log('✅ ComplianceAttestationModal: CHECKOUT REDIRECT FLAG FOUND - Redirecting immediately!');
        
        // User needs to complete checkout - redirect directly to checkout
        const plan = planFromStorage || sessionStorage.getItem('checkout_plan') || 'starter';
        console.log('  - Using plan:', plan);
        
        // Set a flag to prevent MainLayout from redirecting during checkout creation
        sessionStorage.setItem('redirecting_to_checkout', 'true');
        
        // Clear flags immediately (but keep plan for now in case of error)
        localStorage.removeItem('redirect_to_checkout_after_attestation');
        sessionStorage.removeItem('redirect_to_checkout_after_attestation');
        const planToUse = plan; // Save plan before clearing
        localStorage.removeItem('checkout_plan');
        sessionStorage.removeItem('checkout_plan');
        
        // Mark attestation as complete FIRST to close the modal
        onComplete();
        
        // Create checkout session and redirect IMMEDIATELY
        // Don't use async IIFE - just fire and forget, redirect immediately when ready
        console.log('🚀 ComplianceAttestationModal: Creating checkout session NOW...');
        api.billing.createCheckoutSession(
          planToUse,
          `${window.location.origin}/billing/checkout-success?session_id={CHECKOUT_SESSION_ID}`,
          `${window.location.origin}/billing`
        ).then((checkoutResponse) => {
          console.log('✅ ComplianceAttestationModal: Checkout session created!', checkoutResponse);
          
          if (checkoutResponse?.data?.checkout_url) {
            console.log('🔗 ComplianceAttestationModal: Redirecting to checkout URL:', checkoutResponse.data.checkout_url);
            // Use window.location.replace() for immediate redirect that can't be intercepted
            window.location.replace(checkoutResponse.data.checkout_url);
          } else {
            console.error('❌ ComplianceAttestationModal: No checkout URL in response:', checkoutResponse);
            sessionStorage.removeItem('redirecting_to_checkout');
            toast.error('Failed to get checkout URL. Please try again from the billing page.');
            navigate('/billing');
          }
        }).catch((checkoutError: any) => {
          console.error('❌ ComplianceAttestationModal: Failed to create checkout session:', checkoutError);
          console.error('  - Error status:', checkoutError.response?.status);
          console.error('  - Error data:', checkoutError.response?.data);
          console.error('  - Error message:', checkoutError.message);
          sessionStorage.removeItem('redirecting_to_checkout');
          
          // Check if it's an auth error
          if (checkoutError.response?.status === 401) {
            console.error('🔐 ComplianceAttestationModal: Authentication error - redirecting to login');
            toast.error('Session expired. Please log in again.');
            localStorage.setItem('redirect_to_checkout_after_login', 'true');
            localStorage.setItem('checkout_plan_after_login', planToUse);
            navigate('/login');
          } else {
            toast.error(`Failed to create checkout: ${checkoutError.response?.data?.detail || checkoutError.message}`);
            navigate('/billing');
          }
        });
        
        // Return immediately - don't wait for checkout creation
        return;
      }
      
      // If no checkout redirect flag, check normal attestation flow
      console.log('ComplianceAttestationModal: No checkout flag, checking normal flow');
      
      // Check if subscription was created or if plan selection is needed
      if (data.subscription_created) {
        toast.success('Compliance attestation recorded and trial subscription started!');
      } else if (data.needs_plan_selection) {
        toast.info('Compliance attestation recorded. Please select a plan to continue.');
        // Redirect to billing page for plan selection
        onComplete();
        setTimeout(() => {
          navigate('/billing');
        }, 500);
        return;
      } else {
        toast.success('Compliance attestation recorded successfully');
      }
      
      onComplete();
    } catch (err: any) {
      console.error('Failed to submit attestation:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to submit attestation. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <Shield className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-2xl">Compliance Attestation Required</DialogTitle>
              <DialogDescription className="text-base mt-1">
                Please review and accept the SMS/TCPA compliance terms to continue
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This attestation is legally binding. Please read carefully before accepting.
          </AlertDescription>
        </Alert>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-h-96 overflow-y-auto">
          <pre className="whitespace-pre-wrap text-sm font-mono text-gray-700 leading-relaxed">
            {TCPA_ATTESTATION_TEXT}
          </pre>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Checkbox
            id="agree"
            checked={agreed}
            onCheckedChange={(checked) => setAgreed(checked as boolean)}
            className="mt-1"
          />
          <label
            htmlFor="agree"
            className="text-sm font-medium leading-relaxed cursor-pointer select-none"
          >
            I have read and understood the compliance requirements above. I agree to these terms and
            confirm that I have obtained, or will obtain, proper consent from all contacts before
            sending SMS messages. I understand that this attestation is legally binding and that
            violations may result in account suspension and legal liability.
          </label>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            onClick={handleSubmit}
            disabled={!agreed || isSubmitting}
            size="lg"
            className="px-8"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Submitting...
              </div>
            ) : (
              'Accept and Continue'
            )}
          </Button>
        </div>

        <p className="text-xs text-center text-gray-500">
          By clicking "Accept and Continue", your acceptance will be recorded with a timestamp, IP
          address, and user agent for compliance purposes.
        </p>
      </DialogContent>
    </Dialog>
  );
};

